import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import axiosPrivate from 'config/axiosPrivate';
import { useWebsiteFilter } from 'utils/hooks/useWebsiteFilter';

import ProjectTextsTable from 'components/shared/projecttexttable/ProjectTextsTable';
import ProjectDetailsCard from 'components/shared/ProjectDetailsCard';
import { Loader } from 'components/shared/Loader';
import ErrorComponent from 'components/shared/ErrorComponent';
import { CONSULTANT_PROJECT_ROUTES } from 'pages/consultant/projects/constants';
import usePageTracking from 'utils/hooks/usePageTracking';

const CONSULTANT_PROJECT_DETAILS_PAGE = CONSULTANT_PROJECT_ROUTES.DETAILS;

export default function ConsultantProjectDisplay() {
  usePageTracking(CONSULTANT_PROJECT_DETAILS_PAGE);
  const { t } = useTranslation();
  const history = useHistory();
  const [selectedWebsiteUrl] = useWebsiteFilter();

  const [order, setOrder] = useState(null);
  const [textList, setTextList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isTextLoading, setIsTextLoading] = useState(true);
  const [isDetailsOpen, setIsDetailsOpen] = useState(true);
  const [faviconUrl, setFaviconUrl] = useState('');
  const [websiteDomain, setWebsiteDomain] = useState('');
  const [generationMessage, setGenerationMessage] = useState('');
  const [isGenerationError, setIsGenerationError] = useState(false);

  useEffect(() => {
    document.title = t('Typix - Détails du projet');
    const previousBodyBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#FAFAFA';
    window.dispatchEvent(new Event('locationchange'));

    return () => {
      document.body.style.backgroundColor = previousBodyBg;
    };
  }, [t]);

  useEffect(() => {
    const fetchProjectData = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        let projectId = urlParams.get('id');
        if (!projectId) {
          projectId = window.localStorage.getItem('AdminClient_ProjectDisplay_id');
        }

        if (!projectId) {
          setIsError(true);
          return;
        }

        setIsLoading(true);
        setIsError(false);
        const response = await axiosPrivate.get(`/project/${projectId}`);
        if (response.status === 200) {
          setOrder(response.data);
        }
      } catch (error) {
        setIsError(true);
        console.error('Error fetching project (Consultant):', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjectData();
  }, []);

  useEffect(() => {
    const fetchTexts = async () => {
      if (!order?.id) return;

      try {
        setIsTextLoading(true);
        const response = await axiosPrivate.get(`/texts/external/by-project/${order.id}`);
        if (response.status === 200) {
          setTextList(response.data || []);
        }
      } catch (error) {
        console.error('Error fetching texts:', error);
        setTextList([]);
      } finally {
        setIsTextLoading(false);
      }
    };

    fetchTexts();
  }, [order?.id]);

  useEffect(() => {
    const toDomain = (u) =>
      (u || '').replace(/^https?:\/\//i, '').replace(/\/.*$/, '').trim();

    const websiteUrl = order?.website?.urlSite || order?.website?.url || selectedWebsiteUrl || '';

    if (!websiteUrl) {
      setFaviconUrl('');
      setWebsiteDomain('');
      return;
    }

    const domain = toDomain(websiteUrl);
    setWebsiteDomain(domain);

    if (!domain) {
      setFaviconUrl('');
      return;
    }

    setFaviconUrl(
      `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=64`
    );
  }, [order?.website?.urlSite, order?.website?.url, selectedWebsiteUrl]);

  useEffect(() => {
    if (!order?.id) return undefined;

    const generationStorageKey = `AC_Generation_${order.id}`;
    const generationEventName = 'ac-generation-changed';

    const syncFromStorage = () => {
      try {
        const val = window.localStorage.getItem(generationStorageKey);
        const errorMessage = window.localStorage.getItem(`${generationStorageKey}_error`);
        if (val === '1') {
          setIsGenerationError(false);
          const normalizedOrderTextType = ((order?.textType || '') + '').toUpperCase();
          const translationIndicators = new Set(['TRANSLATION', 'TRANSLATED', 'EDITORIAL_AND_TRANSLATION']);
          const isTranslationProject = translationIndicators.has(normalizedOrderTextType);

          if (isTranslationProject) {
            setGenerationMessage(t('Traduction en cours...'));
          } else if (order?.contentType === 'OPTIMIZE') {
            setGenerationMessage(t('Optimisation en cours...'));
          } else {
            setGenerationMessage(t('Génération en cours...'));
          }
        } else if (errorMessage) {
          setIsGenerationError(true);
          setGenerationMessage(errorMessage);
        } else {
          setIsGenerationError(false);
          setGenerationMessage('');
        }
      } catch {
        setIsGenerationError(false);
        setGenerationMessage('');
      }
    };

    syncFromStorage();
    window.addEventListener(generationEventName, syncFromStorage);
    return () => window.removeEventListener(generationEventName, syncFromStorage);
  }, [order?.id, order?.textType, order?.contentType, t]);

  useEffect(() => {
    if (!order?.id) return undefined;

    let cancelled = false;
    const generationStorageKey = `AC_Generation_${order.id}`;

    const refreshTexts = async () => {
      try {
        const response = await axiosPrivate.get(`/texts/external/by-project/${order.id}`);
        if (!cancelled && response.status === 200) {
          setTextList(response.data || []);
        }
      } catch (error) {
        console.error('Error fetching texts:', error);
      }
    };

    const pollGeneration = async () => {
      try {
        const response = await axiosPrivate.get(`/project/job-status/${order.id}`);
        const jobs = Array.isArray(response.data) ? response.data : [];
        const generationFailed =
          jobs.length > 0 &&
          jobs.every(
            (job) => String(job?.jobStatus || '').trim().toUpperCase() === 'FAILED'
          );

        if (response.status === 200 && !generationFailed) {
          window.localStorage.removeItem(`${generationStorageKey}_error`);
          window.localStorage.setItem(generationStorageKey, '1');
        } else {
          if (generationFailed) {
            window.localStorage.setItem(
              `${generationStorageKey}_error`,
              `${t('La génération a échoué.')}\n${t('Veuillez vérifier vos données d’entrée et réessayer. Si le problème persiste, contactez le support.')}`
            );
          } else {
            window.localStorage.removeItem(`${generationStorageKey}_error`);
          }
          window.localStorage.setItem(generationStorageKey, '0');
        }
        window.dispatchEvent(new Event('ac-generation-changed'));
        await refreshTexts();
      } catch (error) {
        console.error('Error polling generation status:', error);
      }
    };

    pollGeneration();
    const intervalId = setInterval(pollGeneration, 30000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [order?.id, t]);

  const totalTextsCount = Array.isArray(textList) ? textList.length : 0;
  const onlineTextsCount = Array.isArray(textList)
    ? textList.filter((text) => text?.deployed).length
    : 0;
  const validatedTextsCount = Array.isArray(textList)
    ? textList.filter((text) => text?.validated && !text?.deployed).length
    : 0;
  const deliveredTextsCount = Array.isArray(textList)
    ? textList.filter((text) => text?.delivered && !text?.validated).length
    : 0;
  const draftTextsCount = Array.isArray(textList)
    ? textList.filter(
      (text) => !text?.delivered && !text?.validated && !text?.deployed
    ).length
    : 0;

  const textsTableProps = {
    texts: textList,
    isDetailsOpen,
    orderId: order?.id,
    generationMessage,
    isGenerationError,
    orderTextAmount: order?.textAmount,
    totalTextsCount,
    readOnly: true,
    textDetailsPath: CONSULTANT_PROJECT_ROUTES.TEXT_DETAILS,
  };

  if (isLoading) return <Loader />;
  if (isError || !order) return <ErrorComponent />;

  return (
    <div style={{ backgroundColor: '#FAFAFA' }}>
      <div
        style={{
          padding: '6px 16px',
          marginTop: '-15px',
          marginLeft: '-18px',
          borderBottom: '1px solid #E0E0E0',
          fontSize: '14px',
          color: '#9E9E9E',
        }}
      >
        <div style={{ marginBottom: '8px' }}>
          <button
            type="button"
            onClick={() => history.goBack()}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              margin: 0,
              fontWeight: 500,
              color: '#000000',
              cursor: 'pointer',
            }}
          >
            {t('Projet')}
          </button>
          <span> &gt; </span>
          <span>{websiteDomain || order?.website?.urlSite || ''}</span>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          padding: '12px 16px',
          marginLeft: '-10px',
          marginTop: '15px',
          marginBottom: '-18px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '4px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '35px', fontWeight: 600, color: '#000000' }}>
              {order?.title || ''}
            </span>
            {order?.type && (
              <span
                style={{
                  padding: '4px 12px',
                  borderRadius: '999px',
                  border: '1px solid #E0E0E0',
                  fontSize: '11px',
                  color: '#757575',
                  backgroundColor: '#FFFFFF',
                  lineHeight: 1.4,
                }}
              >
                {order.type}
              </span>
            )}
          </div>

          {websiteDomain && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                marginTop: 4,
                marginLeft: '-10px',
              }}
            >
              <div
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {faviconUrl ? (
                  <img
                    src={faviconUrl}
                    alt={websiteDomain}
                    style={{ width: 44, height: 44, borderRadius: 10 }}
                  />
                ) : (
                  <span style={{ fontSize: 22, fontWeight: 600, color: '#FFFFFF' }}>
                    {(websiteDomain[0] || 'W').toUpperCase()}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                  {websiteDomain}
                </span>
                {totalTextsCount > 0 && (
                  <span style={{ fontSize: 12, fontWeight: 400, color: '#6B7280', marginTop: 2 }}>
                    {t('{{online}} textes en lignes sur {{total}}', {
                      online: onlineTextsCount,
                      total: totalTextsCount,
                    })}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {isTextLoading ? (
        <div style={{ padding: '32px 16px' }}>
          <Loader />
        </div>
      ) : (
        Array.isArray(textList) &&
        (isDetailsOpen ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 24,
              padding: '0 16px 32px',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <ProjectTextsTable {...textsTableProps} />
            </div>

            <ProjectDetailsCard
              order={order}
              isOpen={isDetailsOpen}
              onToggle={setIsDetailsOpen}
              onlineTextsCount={onlineTextsCount}
              totalTextsCount={totalTextsCount}
              validatedTextsCount={validatedTextsCount}
              deliveredTextsCount={deliveredTextsCount}
              draftTextsCount={draftTextsCount}
              hideAttribution={false}
            />
          </div>
        ) : (
          <div style={{ position: 'relative', padding: '0 16px 32px' }}>
            <ProjectTextsTable {...textsTableProps} />

            <div style={{ position: 'absolute', top: -64, right: 16 }}>
              <ProjectDetailsCard
                order={order}
                isOpen={isDetailsOpen}
                onToggle={setIsDetailsOpen}
                onlineTextsCount={onlineTextsCount}
                totalTextsCount={totalTextsCount}
                validatedTextsCount={validatedTextsCount}
                deliveredTextsCount={deliveredTextsCount}
                draftTextsCount={draftTextsCount}
                hideAttribution={false}
              />
            </div>
          </div>
        ))
      )}

    </div>
  );
}
